import { useEffect, useId, useRef, useState } from "react";

/**
 * RazorpayAffordability
 * ---------------------
 * Display-only widget that shows Razorpay affordability options (EMI, Pay Later,
 * Cardless EMI, offers) based on the current product price.
 *
 * Phase 1 behaviour:
 *  - No checkout prefill (does not touch Checkout.jsx).
 *  - Silent fallback: renders null if key is missing, price is invalid,
 *    product is disabled, or the Razorpay CDN script fails to load.
 *  - Script loaded once per page via id guard.
 *  - Container cleared on re-render / unmount to prevent duplicate widgets
 *    when variant or product changes.
 */

const SCRIPT_ID = "rzp-affordability-script";
const SCRIPT_URL = "https://cdn.razorpay.com/widgets/affordability/affordability.js";

function loadScript() {
    return new Promise((resolve) => {
        if (typeof window === "undefined") return resolve(false);
        if (window.RazorpayAffordabilitySuite) return resolve(true);

        let s = document.getElementById(SCRIPT_ID);
        if (s) {
            s.addEventListener("load", () => resolve(!!window.RazorpayAffordabilitySuite), { once: true });
            s.addEventListener("error", () => resolve(false), { once: true });
            // If already loaded and class attached, resolve straight away next tick.
            if (window.RazorpayAffordabilitySuite) return resolve(true);
            return;
        }

        s = document.createElement("script");
        s.id = SCRIPT_ID;
        s.src = SCRIPT_URL;
        s.async = true;
        s.defer = true;
        s.addEventListener("load", () => resolve(!!window.RazorpayAffordabilitySuite), { once: true });
        s.addEventListener("error", () => resolve(false), { once: true });
        document.head.appendChild(s);
    });
}

export default function RazorpayAffordability({ keyId, amount, disabled }) {
    // Unique container id per mount so re-mounts / multiple PDPs don't collide.
    const autoId = useId().replace(/[:]/g, "");
    const containerId = `rzp-aff-${autoId}`;
    const containerRef = useRef(null);
    const [ready, setReady] = useState(false);
    // `hasOffers` is null = undecided, true = widget rendered real content,
    // false = widget collapsed (no offers returned). Drives the fallback text.
    const [hasOffers, setHasOffers] = useState(null);

    const validKey = typeof keyId === "string" && keyId.length > 0;
    const validAmount = Number.isFinite(amount) && amount > 0;
    const shouldRender = validKey && validAmount && !disabled;

    useEffect(() => {
        if (!shouldRender) return undefined;
        let cancelled = false;
        (async () => {
            const ok = await loadScript();
            if (!cancelled && ok) setReady(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [shouldRender]);

    useEffect(() => {
        if (!ready || !shouldRender) return undefined;
        const host = containerRef.current;
        if (!host) return undefined;

        // Clear any previous widget DOM before re-rendering (amount change, variant swap).
        host.innerHTML = "";
        setHasOffers(null);

        // Pass a plain (non-React-managed) DOM element as the Razorpay `target`.
        // If we pass the React-ref node directly, Razorpay's SDK can hit the
        // element's __reactFiber$ property during JSON.stringify and throw
        // "Converting circular structure to JSON". A fresh div created here is
        // free of React fiber refs.
        const plainTarget = document.createElement("div");
        plainTarget.style.width = "100%";
        host.appendChild(plainTarget);

        let suite = null;
        try {
            const Ctor = window.RazorpayAffordabilitySuite;
            if (typeof Ctor !== "function") return undefined;
            suite = new Ctor({
                key: keyId,
                amount: Math.round(amount),
                currency: "INR",
                target: plainTarget,
            });
            if (suite && typeof suite.render === "function") {
                suite.render();
            }
        } catch (_) {
            // Silent failure — widget is nice-to-have, never crash the PDP.
        }

        // After the SDK has had a chance to call the eligibility API and render
        // (or collapse), check whether the iframe has real visible content.
        // Three probes (3s / 6s / 9s) catch:
        //  - fast renders on desktop
        //  - the typical 5–6s mobile render
        //  - the rare slow path where Razorpay flips from skeleton to "no offers"
        //    after the eligibility API responds late
        const settle = () => {
            try {
                const iframe = plainTarget.querySelector('iframe[id*="rzpAffordability"]');
                // > 80 height ensures we don't mistake the loading skeleton for
                // real content. Width threshold is intentionally low because the
                // host is now constrained by max-width: 28rem, so the iframe
                // measures ~430px on desktop and full container width on mobile.
                const visible = iframe && iframe.offsetHeight > 80;
                setHasOffers(!!visible);
            } catch (_) {
                setHasOffers(false);
            }
        };
        const probeMid = setTimeout(settle, 3000);
        const probeLate = setTimeout(settle, 6000);
        const probeFinal = setTimeout(settle, 9000);

        return () => {
            clearTimeout(probeMid);
            clearTimeout(probeLate);
            clearTimeout(probeFinal);
            try {
                if (host) host.innerHTML = "";
            } catch (_) {
                /* ignore */
            }
        };
    }, [ready, shouldRender, keyId, amount, containerId]);

    if (!shouldRender) return null;

    // While loading (hasOffers === null) and when offers are present, show the
    // light card. When the SDK confirms no offers, collapse the card visually
    // (CSS hidden — never unmounted) so future variant/amount changes can still
    // re-mount the widget into the same container ref.
    const showCard = hasOffers !== false;

    return (
        <div className="w-full" data-testid="razorpay-affordability">
            <div
                className={showCard ? "" : "hidden"}
                aria-hidden={!showCard}
            >
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold mb-2">
                    Affordability
                </div>
                {/* Compact light card.
                    IMPORTANT: the host MUST be a `block` element with a measurable
                    width during initialization. Razorpay's SDK reads
                    `target.getBoundingClientRect().width` at construction time;
                    if it sees 0 (e.g. inline-block + fit-content on an empty node)
                    the iframe is created at 0 width and the widget appears blank.
                    We give it a stable max-width container so the card stays
                    compact without starving the SDK of a measurable width. */}
                <div
                    id={containerId}
                    ref={containerRef}
                    className="razorpay-affordability-container block w-full md:max-w-md rounded-md bg-white px-2 py-1.5 border border-[#00BCE5]/35 overflow-hidden"
                />
            </div>
            {hasOffers === false && (
                <div
                    className="text-xs md:text-sm text-white/55 font-display tracking-wide border border-dashed border-[#27272A] rounded-sm px-4 py-3 bg-[#0A0A0B]"
                    data-testid="razorpay-affordability-fallback"
                >
                    EMI / card offers may be available at checkout.
                </div>
            )}
        </div>
    );
}
